import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import { FormSelect } from "@/components/Base/Form";
import { Dialog } from "@/components/Base/Headless";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { getToken } from "@/utils/auth";

type Team = { id: string; name: string; status?: boolean };
type BankInSummary = {
  teamSalesId: string;
  salesDate: string;
  bankedAt: string | null;
  bankInAmount: number | null;
  bankInDifference: number;
  receiptCount: number;
  hasReceipt: boolean;
};
type BankSlip = {
  id: string;
  imageUrl: string;
  s3Key: string;
  createdAt: string;
};
type BankReceiptDetails = {
  teamSalesId: string;
  teamId: string;
  teamName: string | null;
  salesDate: string;
  bankedAt: string;
  bankInAmount: number | null;
  bankInDifference: number;
  receiptCount: number;
  bankSlips: BankSlip[];
};
type Row = {
  teamId?: string | null;
  teamName?: string | null;
  productId: string;
  product: string;
  sku: string | null;
  productType?: string | null;
  price: number;
  totalPackSold: number;
  totalValue: number;
  reimbValue: number;
  totalValuePlusReimb: number;
  byDate: Record<string, { packSold: number | null; value: number | null }>;
};
type TeamGroup = { key: string; teamId: string | null; name: string; rows: Row[] };
const PRODUCT_TYPE_ORDER = ["Cigarette", "Device", "Refills", "Accessories"] as const;

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function formatDateHeading(ymd: string) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function formatDateTime(v: string | null | undefined) {
  if (!v) return "-";
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function money(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function moneyWithThousands(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function renderPack(v: number | null | undefined) {
  return v === null || v === undefined ? "-" : String(v);
}

function renderMoney(v: number | null | undefined) {
  return v === null || v === undefined ? "-" : money(v);
}

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

function sortTypeGroups(groups: Map<string, Row[]>) {
  return Array.from(groups.entries()).sort((a, b) => {
    const ai = PRODUCT_TYPE_ORDER.indexOf(a[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
    const bi = PRODUCT_TYPE_ORDER.indexOf(b[0] as (typeof PRODUCT_TYPE_ORDER)[number]);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a[0].localeCompare(b[0]);
  });
}

export default function TeamSalesDataPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultStart = dateToYMD(monthStart);
  const defaultEnd = dateToYMD(today);

  const [dateRange, setDateRange] = useState(`${defaultStart} - ${defaultEnd}`);
  const [teamId, setTeamId] = useState("");

  const [teams, setTeams] = useState<Team[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [bankInByTeamDate, setBankInByTeamDate] = useState<Record<string, BankInSummary>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedTypeNames, setExpandedTypeNames] = useState<Set<string>>(new Set());
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");
  const [receiptDetails, setReceiptDetails] = useState<BankReceiptDetails | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/teams?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list: Team[] = Array.isArray(res.data?.data) ? res.data.data : [];
        setTeams(list.filter((t) => t.status !== false).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        // keep page usable without teams list
      }
    })();
  }, [apiUrl, token]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const parsedRange = parseDateRangeInput(dateRange);
      if (!parsedRange) {
        setError("Please select a valid date range.");
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      params.set("startDate", parsedRange.startDate);
      params.set("endDate", parsedRange.endDate);
      if (teamId) params.set("teamId", teamId);

      const res = await axios.get(`${apiUrl}/team-sales-data?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDates(Array.isArray(res.data?.dates) ? res.data.dates : []);
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      setBankInByTeamDate(res.data?.bankInByTeamDate && typeof res.data.bankInByTeamDate === "object" ? res.data.bankInByTeamDate : {});
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load Team Sales Data.");
      setDates([]);
      setRows([]);
      setBankInByTeamDate({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const isAllTeams = !teamId;
  const colCount = useMemo(() => 3 + dates.length * 2 + 4, [dates.length]);

  const groupedRowsByType = (list: Row[]) => {
    const groups = new Map<string, Row[]>();
    for (const r of list) {
      const typeName = (r.productType || "Other").trim() || "Other";
      const arr = groups.get(typeName) ?? [];
      arr.push(r);
      groups.set(typeName, arr);
    }
    return sortTypeGroups(groups);
  };

  const teamGroups = useMemo<TeamGroup[]>(() => {
    if (!isAllTeams) {
      const selected = teams.find((t) => t.id === teamId);
      return [{ key: teamId || "selected", teamId: teamId || rows[0]?.teamId || null, name: selected?.name || "Selected Team", rows }];
    }

    const byTeam = new Map<string, TeamGroup>();
    for (const r of rows) {
      const key = r.teamId || "unknown";
      const name = r.teamName || "Unknown Team";
      const existing = byTeam.get(key);
      if (existing) {
        existing.rows.push(r);
      } else {
        byTeam.set(key, { key, teamId: r.teamId || null, name, rows: [r] });
      }
    }

    return Array.from(byTeam.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [isAllTeams, rows, teamId, teams]);

  const openReceiptDetails = async (teamIdValue: string, salesDate: string) => {
    setReceiptModalOpen(true);
    setReceiptLoading(true);
    setReceiptError("");
    setReceiptDetails(null);
    try {
      const params = new URLSearchParams();
      params.set("teamId", teamIdValue);
      params.set("salesDate", salesDate);
      const res = await axios.get(`${apiUrl}/team-sales-data/bank-receipts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReceiptDetails((res.data?.data || null) as BankReceiptDetails | null);
    } catch (e: any) {
      setReceiptError(e?.response?.data?.message || "Failed to load bank receipt details.");
    } finally {
      setReceiptLoading(false);
    }
  };

  useEffect(() => {
    setExpandedTypeNames((prev) => {
      const next = new Set(prev);
      for (const team of teamGroups) {
        const grouped = groupedRowsByType(team.rows);
        for (const [typeName] of grouped) {
          next.add(`${team.key}::${typeName}`);
        }
      }
      return next;
    });
  }, [teamGroups]);

  const buildTotals = (list: Row[]) => {
    const totalsByDate: Record<string, { packSold: number; value: number }> = {};
    for (const d of dates) totalsByDate[d] = { packSold: 0, value: 0 };
    for (const r of list) {
      for (const d of dates) {
        totalsByDate[d].packSold += Number(r.byDate?.[d]?.packSold ?? 0);
        totalsByDate[d].value += Number(r.byDate?.[d]?.value ?? 0);
      }
    }
    const grandTotals = list.reduce(
      (acc, r) => {
        acc.totalPackSold += Number(r.totalPackSold ?? 0);
        acc.totalValue += Number(r.totalValue ?? 0);
        acc.reimbValue += Number(r.reimbValue ?? 0);
        acc.totalValuePlusReimb += Number(r.totalValuePlusReimb ?? 0);
        return acc;
      },
      { totalPackSold: 0, totalValue: 0, reimbValue: 0, totalValuePlusReimb: 0 }
    );
    return { totalsByDate, grandTotals };
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Team Sales Data</div>
              {loading ? <Lucide icon="Loader2" className="w-4 h-4 animate-spin text-slate-500 md:ml-3" /> : null}
            </div>

            <div className="flex flex-col gap-8 mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:flex-row sm:items-end gap-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.7fr)_auto] gap-3 w-full sm:w-auto">
                    <div className="sm:w-[420px]">
                      <div className="text-xs text-slate-500 mb-1">Date Range</div>
                      <div className="relative inline-flex w-full">
                        <Lucide
                          icon="Calendar"
                          className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                        />
                        <Litepicker
                          value={dateRange}
                          onChange={(e) => setDateRange((e.target?.value || "").trim())}
                          options={{
                            autoApply: true,
                            singleMode: false,
                            numberOfColumns: 2,
                            numberOfMonths: 2,
                            showWeekNumbers: true,
                            dropdowns: {
                              minYear: 2020,
                              maxYear: null,
                              months: true,
                              years: true,
                            },
                            format: "YYYY-MM-DD",
                          }}
                          className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:w-[170px]">
                      <div className="text-xs text-slate-500 mb-1">Team</div>
                      <FormSelect value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                        <option value="">All Teams</option>
                        {teams.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                      </FormSelect>
                    </div>
                    <div className="sm:self-end">
                    <Button variant="primary" onClick={() => void loadData()} disabled={loading} className="whitespace-nowrap px-4">
                      Apply Filter
                    </Button>
                    </div>
                  </div>
                </div>

                {error ? <div className="px-5 pb-3 text-danger text-sm">{error}</div> : null}

              </div>

              {rows.length === 0 ? (
                <div className="flex flex-col box box--stacked">
                  <div className="px-5 py-6 text-slate-500 text-sm">
                    {loading ? "Loading..." : "No confirmed team sales data found for selected filter."}
                  </div>
                </div>
              ) : (
                teamGroups.map((teamGroup) => (
                  <div key={teamGroup.key} className="flex flex-col box box--stacked">
                    <div className="px-5 py-3 border-b border-slate-200/70 dark:border-darkmode-500">
                      <div className="font-medium">{teamGroup.name}</div>
                    </div>
                    {groupedRowsByType(teamGroup.rows).map(([typeName, typeRows]) => {
                      const sectionKey = `${teamGroup.key}::${typeName}`;
                      const expanded = expandedTypeNames.has(sectionKey);
                      const { totalsByDate, grandTotals } = buildTotals(typeRows);
                      return (
                        <div key={sectionKey}>
                          <div className="px-5 py-3 border-b border-slate-200/70 dark:border-darkmode-500">
                            <button
                              type="button"
                              className="w-full flex items-center gap-2 text-left"
                              onClick={() =>
                                setExpandedTypeNames((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(sectionKey)) next.delete(sectionKey);
                                  else next.add(sectionKey);
                                  return next;
                                })
                              }
                            >
                              <Lucide icon={expanded ? "ChevronDown" : "ChevronRight"} className="w-4 h-4 text-slate-500" />
                              <span className="font-medium">{typeName}</span>
                              <span className="text-xs text-slate-500">({typeRows.length})</span>
                            </button>
                          </div>
                          {expanded ? (
                            <div className="max-w-full overflow-x-auto px-4 pb-4">
                          <Table className="border-b border-slate-200/60 text-xs table-auto w-max min-w-full">
                    <Table.Thead className="text-xs bg-white dark:bg-darkmode-600">
                      <Table.Tr>
                        <Table.Td
                          rowSpan={2}
                          className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-0 z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] pl-3 pr-2 align-middle"
                        >
                          Product
                        </Table.Td>
                        <Table.Td
                          rowSpan={2}
                          className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[220px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] pl-2 pr-2 align-middle"
                        >
                          SKU
                        </Table.Td>
                        <Table.Td
                          rowSpan={2}
                          className="py-2 font-medium bg-slate-50 dark:bg-darkmode-700 sticky left-[350px] z-30 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] text-right pl-2 pr-2 align-middle whitespace-nowrap"
                        >
                          Selling Price
                        </Table.Td>
                        {dates.map((d) => {
                          const summaryKey = teamGroup.teamId ? `${teamGroup.teamId}::${d}` : "";
                          const summary = summaryKey ? bankInByTeamDate[summaryKey] : undefined;
                          return (
                            <Table.Td
                              key={d}
                              colSpan={2}
                              className="py-2 px-2 font-medium text-center bg-slate-50/70 dark:bg-darkmode-700/30 min-w-[170px] whitespace-nowrap"
                            >
                              <div>{formatDateHeading(d)}</div>
                              {summary?.hasReceipt && teamGroup.teamId ? (
                                <button
                                  type="button"
                                  onClick={() => void openReceiptDetails(teamGroup.teamId!, d)}
                                  className="mt-1 inline-flex items-center gap-1 rounded bg-sky-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-[0_0_0_1px_rgba(14,165,233,0.35),0_0_12px_rgba(14,165,233,0.35)] hover:bg-sky-400"
                                  title="View bank-in receipt details"
                                >
                                  <Lucide icon="Receipt" className="w-3 h-3" />
                                  {summary.receiptCount} Bank In Slip{summary.receiptCount > 1 ? "s" : ""}
                                </button>
                              ) : (
                                <div className="mt-1 text-[10px] text-slate-400">No receipt</div>
                              )}
                            </Table.Td>
                          );
                        })}
                        <Table.Td
                          rowSpan={2}
                          className="py-2 px-2 font-medium text-center bg-success/20 dark:bg-success/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[100px] whitespace-normal leading-tight"
                        >
                          Total Pack
                          <br />
                          Sold
                        </Table.Td>
                        <Table.Td
                          rowSpan={2}
                          className="py-2 px-2 font-medium text-center bg-warning/20 dark:bg-warning/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-normal leading-tight"
                        >
                          Total Value
                          <br />
                          (RM)
                        </Table.Td>
                        <Table.Td
                          rowSpan={2}
                          className="py-2 px-2 font-medium text-center bg-warning/30 dark:bg-warning/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-normal leading-tight"
                        >
                          Reimb Value
                          <br />
                          (RM)
                        </Table.Td>
                        <Table.Td
                          rowSpan={2}
                          className="py-2 px-2 font-medium text-center bg-primary/15 dark:bg-primary/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[130px] whitespace-normal leading-tight"
                        >
                          Total Value
                          <br />+ Reimb
                          <br />
                          (RM)
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        {dates.flatMap((d) => [
                          <Table.Td
                            key={`${d}-pack`}
                            className="py-2 px-2 text-center font-medium bg-success/10 dark:bg-success/10 whitespace-nowrap min-w-[85px] w-[85px]"
                          >
                            Pack Sold
                          </Table.Td>,
                          <Table.Td
                            key={`${d}-value`}
                            className="py-2 px-2 text-center font-medium bg-warning/10 dark:bg-warning/10 whitespace-nowrap min-w-[85px] w-[85px]"
                          >
                            Value (RM)
                          </Table.Td>,
                        ])}
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {typeRows.map((r) => (
                          <Table.Tr key={`${r.teamId || "none"}-${r.productId}`} className="[&_td]:border-b [&_td]:border-slate-200/60">
                            <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] truncate pl-3 pr-2">
                              {r.product}
                            </Table.Td>
                            <Table.Td className="py-2 bg-slate-50 dark:bg-darkmode-700 whitespace-nowrap sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] truncate pl-2 pr-2">
                              {r.sku || "-"}
                            </Table.Td>
                            <Table.Td className="py-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-50 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] pl-2 pr-2 whitespace-nowrap">
                              {money(r.price)}
                            </Table.Td>
                            {dates.flatMap((d) => [
                              <Table.Td
                                key={`${r.productId}-${d}-pack`}
                                className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-success/10 dark:bg-success/10 whitespace-nowrap"
                              >
                                {renderPack(r.byDate?.[d]?.packSold)}
                              </Table.Td>,
                              <Table.Td
                                key={`${r.productId}-${d}-value`}
                                className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-warning/10 dark:bg-warning/10 whitespace-nowrap"
                              >
                                {renderMoney(r.byDate?.[d]?.value)}
                              </Table.Td>,
                            ])}
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/20 dark:bg-success/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[100px] whitespace-nowrap">
                              {r.totalPackSold ?? 0}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/20 dark:bg-warning/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(r.totalValue ?? 0)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/30 dark:bg-warning/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                              {moneyWithThousands(r.reimbValue ?? 0)}
                            </Table.Td>
                            <Table.Td className="py-2 px-2 text-center tabular-nums bg-primary/15 dark:bg-primary/20 border-l border-slate-200 dark:border-darkmode-400 min-w-[130px] whitespace-nowrap">
                              {moneyWithThousands(r.totalValuePlusReimb ?? 0)}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      {typeRows.length > 0 ? (
                        <Table.Tr className="font-medium [&_td]:border-b [&_td]:border-slate-300/80 dark:[&_td]:border-darkmode-400">
                          <Table.Td className="py-2 bg-slate-100 dark:bg-darkmode-700 sticky left-0 z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[220px] max-w-[280px] pl-3 pr-2 whitespace-nowrap">
                            Total
                          </Table.Td>
                          <Table.Td className="py-2 bg-slate-100 dark:bg-darkmode-700 sticky left-[220px] z-20 border-r border-slate-200 dark:border-darkmode-400 min-w-[130px] max-w-[180px] pl-2 pr-2 whitespace-nowrap">
                            -
                          </Table.Td>
                          <Table.Td className="py-2 text-right tabular-nums sticky left-[350px] z-20 bg-slate-100 dark:bg-darkmode-700 border-r border-slate-200 dark:border-darkmode-400 min-w-[95px] w-[95px] pl-2 pr-2 whitespace-nowrap">
                            -
                          </Table.Td>
                          {dates.flatMap((d) => [
                            <Table.Td
                              key={`total-${d}-pack`}
                              className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-success/20 dark:bg-success/20 whitespace-nowrap"
                            >
                              {totalsByDate[d]?.packSold ?? 0}
                            </Table.Td>,
                            <Table.Td
                              key={`total-${d}-value`}
                              className="py-2 px-2 text-center tabular-nums min-w-[85px] w-[85px] bg-warning/20 dark:bg-warning/20 whitespace-nowrap"
                            >
                              {moneyWithThousands(totalsByDate[d]?.value ?? 0)}
                            </Table.Td>,
                          ])}
                          <Table.Td className="py-2 px-2 text-center tabular-nums bg-success/30 dark:bg-success/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[100px] whitespace-nowrap">
                            {grandTotals.totalPackSold}
                          </Table.Td>
                          <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/30 dark:bg-warning/30 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                            {moneyWithThousands(grandTotals.totalValue)}
                          </Table.Td>
                          <Table.Td className="py-2 px-2 text-center tabular-nums bg-warning/40 dark:bg-warning/40 border-l border-slate-200 dark:border-darkmode-400 min-w-[110px] whitespace-nowrap">
                            {moneyWithThousands(grandTotals.reimbValue)}
                          </Table.Td>
                          <Table.Td className="py-2 px-2 text-center tabular-nums bg-primary/25 dark:bg-primary/25 border-l border-slate-200 dark:border-darkmode-400 min-w-[130px] whitespace-nowrap">
                            {moneyWithThousands(grandTotals.totalValuePlusReimb)}
                          </Table.Td>
                        </Table.Tr>
                      ) : null}
                    </Table.Tbody>
                  </Table>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <Dialog
          open={receiptModalOpen}
          onClose={() => {
            setReceiptModalOpen(false);
            setReceiptError("");
            setReceiptDetails(null);
          }}
        >
          <Dialog.Panel className="max-w-3xl">
            <Dialog.Title className="flex items-center justify-between">
              <div className="font-medium">Bank-In Receipt Details</div>
            </Dialog.Title>
            <Dialog.Description className="px-5 py-4 space-y-3">
              {receiptLoading ? (
                <div className="text-sm text-slate-500">Loading receipt details...</div>
              ) : receiptError ? (
                <div className="text-sm text-danger">{receiptError}</div>
              ) : receiptDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Team:</span> {receiptDetails.teamName || "-"}</div>
                    <div><span className="text-slate-500">Sales Date:</span> {formatDateHeading(receiptDetails.salesDate)}</div>
                    <div><span className="text-slate-500">Submitted At:</span> {formatDateTime(receiptDetails.bankedAt)}</div>
                    <div><span className="text-slate-500">Receipts:</span> {receiptDetails.receiptCount}</div>
                    <div><span className="text-slate-500">Bank In Amount:</span> {receiptDetails.bankInAmount === null ? "-" : moneyWithThousands(receiptDetails.bankInAmount)}</div>
                    <div><span className="text-slate-500">Difference:</span> {moneyWithThousands(receiptDetails.bankInDifference || 0)}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium mb-2">Submitted Receipt Image(s)</div>
                    {receiptDetails.bankSlips.length === 0 ? (
                      <div className="text-sm text-slate-500">No receipt image found.</div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {receiptDetails.bankSlips.map((s) => (
                          <a
                            key={s.id}
                            href={s.imageUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="block border border-slate-200 rounded-md overflow-hidden hover:border-primary/60"
                          >
                            <img src={s.imageUrl} alt="Bank receipt" className="w-full h-48 object-cover bg-slate-50" />
                            <div className="px-2 py-1.5 text-[11px] text-slate-500">{formatDateTime(s.createdAt)}</div>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">No receipt details available.</div>
              )}
            </Dialog.Description>
            <Dialog.Footer>
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setReceiptModalOpen(false);
                  setReceiptError("");
                  setReceiptDetails(null);
                }}
              >
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      </RoleGate>
    </ProtectedRoute>
  );
}

